<template>
  <div :class="['product-details-media', { 'product-details-media--large-media': isLargeMedia }]">
    <div class="product-details-media__container">
      <!-- THUMBS -->
      <div class="product-details-media__thumbs">
        <oie-base-image
          v-for="(item, i) in mediaAssets"
          :key="`product-details-media__thumb-${i}`"
          :class="['product-details-media__thumb', { 'product-details-media__thumb--active': i === activeIndex }]"
          :images="item.type === 'orbitvu' ? item.imageUrl : `${item.imageUrl}?preset=ProductSmall&watermark=OIEWatermarkSmall`"
          :alt-text="item.altText ? `${item.altText.trim()}` : productAltText"
          @click.native="activeIndex = i">
        </oie-base-image>
      </div>

      <!-- CONTENT -->
      <div class="product-details-media__content">
        <!-- MEDIA -->
        <div class="product-details-media__media">
          <!-- ZOOM TOGGLE -->
          <oie-base-action
            v-if="mq('medium')"
            v-enlarge-media
            round
            size="xlarge"
            theme="skeleton"
            class="product-details-media__enlarge"
            :aria-label="isLargeMedia ? tStatic('genericZoomOut') : tStatic('genericZoomIn')">
            <oie-base-icon size="l" :icon="isLargeMedia ? 'zoom-out' : 'zoom-in'"></oie-base-icon>
          </oie-base-action>

          <!-- NEXT/PREV BTNS-->
          <template v-if="mediaAssets.length > 1">
            <oie-base-action
              round
              theme="outlined-black"
              class="product-details-media__btn product-details-media__btn--prev"
              @click="changeImage('prev')">
              <oie-base-icon icon="chevron-left"></oie-base-icon>
            </oie-base-action>
            <oie-base-action
              round
              theme="outlined-black"
              class="product-details-media__btn product-details-media__btn--next"
              @click="changeImage()">
              <oie-base-icon icon="chevron-right"></oie-base-icon>
            </oie-base-action>
          </template>
          <!-- MEDIA ASSETS -->
          <div
            v-for="(asset, index) in mediaAssets"
            :key="`media-asset-${index}`"
            :class="['product-details-media__media-item', { 'product-details-media__media-item--active': index === activeIndex }]">
            <div v-if="asset.type === 'orbitvu'">
              <div :id="'ovContent-' + product.videoAssetUrl" class="orbitvu-container"></div>
            </div>

            <oie-base-image
              v-else-if="asset.type === 'image'"
              v-enlarge-media
              :images="getImageUrl(asset.imageUrl)"
              :use-lazy-loading="index !== 0"
              :alt-text="asset.altText ? `${asset.altText.trim()}` : productAltText"></oie-base-image>

            <oie-video-player
              v-else-if="asset.type === 'video'"
              :cloudflare-id="asset.vendor === 'cloudflare' ? asset.id : ''"
              :youtube-id="asset.vendor === 'youtube' ? asset.id : ''"
              :poster-image="getImageUrl(asset.imageUrl)">
            </oie-video-player>

            <oie-base-image
              v-else
              :images="`${getAssetPath('/globalassets/missing404/404.png')}?preset=ProductLarge`"
              :alt-text="placeholderAltText"></oie-base-image>
          </div>
        </div>

        <!-- SPECIFICATION -->
        <div
          v-if="specification && activeMediaAsset.specification && specification[activeMediaAsset.specification]"
          class="product-details-media__specification">
          <!-- todo: this is just copy/paste from technical-facts.vu. It should probably be its own component or smth more DRY -->
          <oie-base-card theme="grey">
            <table class="property-table technical-facts__table">
              <caption class="h3-semi-bold">
                {{ activeMediaAsset.specification }}
              </caption>
              <tr
                v-for="(val, key) in specification[activeMediaAsset.specification]"
                :key="key"
                class="property-table__item">
                <td>
                  {{ key }}
                </td>

                <td>
                  {{ val }}
                </td>
              </tr>
            </table>
          </oie-base-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch, onMounted } from '@vue/composition-api'
import _ from 'lodash'

export default defineComponent({
  props: {
    product: {
      type: Object,
      required: false,
      default: null
    },
    images: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  setup(props, { root }) {
    const activeIndex = ref(0)

    const mediaAssets = computed(() => {
      const items = []

      if (isVideoAsset.value) {
        items.push({ type: 'orbitvu', id: props.product.videoAssetUrl, imageUrl: `//static.orbitvu.co/001/${props.product.videoAssetUrl}/iproc/600/600/img01.png` })
      }
      _.forEach(imageUrls.value, (item) => {
        if (_.isObject(item)) {
          items.push({ type: item.videoId ? 'video' : 'image', imageUrl: item.url, specification: item.specification, vendor: item.videoId ? item.videoVendor : null, altText: item.altText ? `${item.altText.trim()}` : null, id: item.videoId ? item.videoId : null })
        } else {
          items.push({ type: 'image', imageUrl: item })
        }
      })

      return items
    })
    const activeMediaAsset = computed(() => mediaAssets.value[activeIndex.value])
    const imageUrls = computed(() => {
      if (props.product) {
        if (props.product.mediaAssetsData) {
          return props.product.mediaAssetsData
        }
        return props.images
      }
    })

    const isVideoAsset = computed(() => props.product && props.product.videoAssetUrl)
    const mediaAssetsData = computed(() => props.product && props.product.mediaAssetsData)
    const isLargeMedia = computed(() => root.$store.getters['ui/isLargeMedia'])
    const specification = ref((props.product && props.product.fullSpecification) ? JSON.parse(props.product.fullSpecification) : null)

    watch(activeIndex, () => {
      root.publish('pause-video')
    })

    function changeImage(direction = 'next') {
      if (direction === 'next') {
        if (activeIndex.value + 1 < mediaAssets.value.length) {
          activeIndex.value++
        } else {
          activeIndex.value = 0
        }
      } else if (direction === 'prev') {
        if (activeIndex.value - 1 >= 0) {
          activeIndex.value--
        } else {
          activeIndex.value = mediaAssets.value.length - 1
        }
      }
    }

    function mountOrbitvuScript() {
      const orbitvu = document.createElement('script')
      orbitvu.setAttribute(
        'src',
        '//orbitvu.co/001/' + props.product.videoAssetUrl + '/ov3601/3/script?width=auto&content2=yes&height=auto&teaser=autorotate'
      )
      document.head.appendChild(orbitvu)
    }

    function getImageUrl(url) {
      if (!url) {
        return `/globalassets/missing404/404.png?preset=ProductSmall`
      } else {
        return !isLargeMedia.value ? `${url}?preset=ProductLarge&watermark=OIEWatermarkLarge` : `${url}?height=740&watermark=OIEWatermarkMaximum`
      }
    }

    onMounted(() => {
      if (isVideoAsset.value) {
        mountOrbitvuScript()
      }
    })

    // Alt text built from what identifies the article - name, article number and manufacturer -
    // for every image the asset itself has no alt text for. Parts that are missing are left out
    // rather than leaving their separator behind, so a product without a brand reads
    // "Sprintbult M597963" and not "Sprintbult M597963 -" (OC-2712).
    const productAltText = computed(() => {
      const product = props.product

      if (!product) {
        return ''
      }

      const name = [product.itemName, product.itemNameSpecification].filter(Boolean).join(' ').trim()
      const identity = [name, product.itemId].filter(Boolean).join(' ').trim()

      return [identity, product.productBrand].filter(Boolean).join(' - ').trim()
    })

    // The generic placeholder is not a picture of the article, so it says so rather than passing
    // itself off as one - but it still names the article, which is what a screen reader needs.
    const placeholderAltText = computed(() => {
      const missing = root.tStatic('productImageMissing') || 'image missing'

      return productAltText.value ? `${productAltText.value} - ${missing}` : missing
    })

    return {
      activeIndex,
      productAltText,
      placeholderAltText,
      mediaAssets,
      activeMediaAsset,
      imageUrls,
      isVideoAsset,
      mediaAssetsData,
      isLargeMedia,
      specification,
      changeImage,
      mountOrbitvuScript,
      getImageUrl
    }
  }
})
</script>

<style lang="scss">
/*------------------------------------*\
   GENERAL
\*------------------------------------*/
.product-details-media {
  --breakpoint-medium: 992px;
  container-type: inline-size;
  container-name: product-details-media;
}

.product-details-media__container {
  display: flex;
  gap: measure(half);

  @container product-details-media (width > 992px) {
    flex-direction: column;
  }

  @include media('to-small') {
    flex-direction: column;
  }
}

/*------------------------------------*\
   THUMBS
\*------------------------------------*/

.product-details-media__thumbs {
  display: flex;
  gap: measure(quarter);
  flex-direction: column;
  flex-wrap: wrap;

  @container product-details-media (width > 992px) {
    flex-direction: row;
  }

  @include media('to-small') {
    flex-direction: row;
  }
}

.product-details-media__thumb {
  width: 4rem;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border: 1px solid color(border-gray);
}

.product-details-media__thumb--active {
  border: 1px solid color(border-blue-1);
}

/*------------------------------------*\
   CONTENT
\*------------------------------------*/

.product-details-media__content {
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: measure(half);

  @container product-details-media (width > 992px) {
    flex-direction: row;
  }
}

/*------------------------------------*\
   CONTENT - media
\*------------------------------------*/
.product-details-media__media {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  padding: 0 measure(quarter);

  @container product-details-media (width > 992px) {
    flex: 1 1 50%;
    aspect-ratio: unset;
  }
}

.product-details-media__media-item {
  width: 100%;
  height: auto;
  display: none;
}

.product-details-media__media-item--active {
  display: block;
}

.product-details-media__media-item > * {
  width: 100%;
}

.product-details-media__media-item .image-element {
  max-height: 22.375rem;
  object-fit: contain;
}

.product-details-media--large-media .product-details-media__media-item .image-element {
  max-height: 44rem;
  object-fit: contain;
}

/*------------------------------------*\
   CONTENT - Specification
\*------------------------------------*/
.product-details-media__specification {
  min-width: 50%;

  @container product-details-media (width > 992px) {
    min-width: unset;
    width: 30%;
  }
}

.product-details-media__specification .technical-facts__table caption {
  margin-bottom: measure(quarter);
}

/*------------------------------------*\
   ACTION BUTTONS
\*------------------------------------*/
.product-details-media__btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 11;
  opacity: 0;
  transition: opacity 0.2s;
}

.product-details-media:hover .product-details-media__btn {
  opacity: 1;
}

.product-details-media__btn--prev {
  left: 0;
}

.product-details-media__btn--next {
  right: 0;
}

.product-details-media__enlarge {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 12;
  color: color(gray-3);
}
</style>
